import { useState, useEffect, useCallback, useMemo, useRef, memo } from "react";
import {
    Box, Flex, HStack, VStack, Text, Heading, Badge, Button, IconButton,
    Input, InputGroup, InputLeftElement, InputRightElement, Tooltip, Spinner, Divider, useToast,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
    Wrap, WrapItem, Tag, TagLabel, Icon, Select,
    Tabs, TabList, TabPanels, Tab, TabPanel, SimpleGrid,
    CircularProgress, CircularProgressLabel,
    AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
} from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    FaExclamationTriangle, FaClock, FaUser, FaSyncAlt, FaSearch, FaTimes,
    FaBoxOpen, FaRegStickyNote, FaCheckCircle, FaRegCopy, FaFileCsv, FaTruck, FaCommentDots,
    FaFileInvoice, FaShoppingCart, FaMapMarkerAlt, FaClipboardCheck, FaBoxes, FaHistory,
    FaQuestionCircle, FaWarehouse,
} from "react-icons/fa";
import { IoMdCart } from "react-icons/io";
import { GoPackage } from "react-icons/go";
import { MdLocalShipping, MdCancel } from "react-icons/md";
import ProdutoImagem from "../components/ProdutoImagem";
import ObservacoesVenda from "../components/ObservacoesVenda";
import ChatMensagensML from "../components/ChatMensagensML";
import {
    getPendenciasVendas, getDetalhesPendencia, moverEstagioPendencia,
    setStatusVenda, setStatusTaga, setMarcadorVenda,
} from "../services/api";

const ACCENT = "#F68633";

// ── Colunas (estágios de resolução) ──────────────────────────
const COLUNAS = [
    { key: "a_resolver",          title: "A Resolver",          scheme: "red",    bg: "#FDECEC", head: "#F7D2D2" },
    { key: "em_tratativa",        title: "Em Tratativa",        scheme: "orange", bg: "#FFF4E6", head: "#FBE1C2" },
    { key: "aguardando_terceiro", title: "Aguardando Terceiro", scheme: "blue",   bg: "#E9F1FD", head: "#CFE0F7" },
    { key: "resolvido",           title: "Resolvido",           scheme: "green",  bg: "#EAF7ED", head: "#CFEBD5" },
];

const formatBRL = (v) =>
    Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

// Data/hora que o produto deveria ter sido enviado (prazo de envio).
const formatPrazo = (iso) =>
    iso ? new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
const formatPrazoLongo = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");

// Há quanto tempo o card está parado no estágio atual.
const tempoDesde = (iso) => {
    if (!iso) return "";
    const horas = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
    if (horas < 1) return "menos de 1h";
    if (horas < 24) return `${horas}h`;
    const dias = Math.floor(horas / 24);
    const resto = horas % 24;
    return resto > 0 ? `${dias}d ${resto}h` : `${dias}d`;
};

const ESTAGIO_DESDE = {
    em_tratativa: { texto: "em tratativa desde", scheme: "blue" },
    aguardando_terceiro: { texto: "aguardando desde", scheme: "orange" },
};

// Intensidade do badge de atraso: quanto mais dias, mais "quente"/escura a cor.
const atrasoMeta = (dias) => {
    if (dias >= 16) return { bg: "red.800", color: "white" };
    if (dias >= 11) return { bg: "red.600", color: "white" };
    if (dias >= 7)  return { bg: "red.500", color: "white" };
    if (dias >= 4)  return { bg: "red.400", color: "white" };
    if (dias >= 2)  return { bg: "orange.400", color: "white" };
    return { bg: "yellow.400", color: "black" }; // 0 a 1 dia
};

const formatAtraso = (card) => {
    if (card.dias_atraso == null) return null;
    if (card.dias_atraso >= 1) return `${card.dias_atraso}d`;
    if (card.horas_atraso >= 1) return `${card.horas_atraso}h`;
    return "<1h";
};

// Saúde da resolução (0-100): piora com atraso/tempo sem resolução.
const saudeMeta = (s) => {
    if (s >= 70) return { scheme: "green", label: "Saudável" };
    if (s >= 45) return { scheme: "yellow", label: "Atenção" };
    if (s >= 20) return { scheme: "orange", label: "Ruim" };
    return { scheme: "red", label: "Crítico" };
};

const origemInfo = (card) => {
    const o = (card.origem || "").toLowerCase();
    if (o.includes("mercado")) return { label: `ML ${card.conta || ""}`.trim(), scheme: String(card.conta) === "2" ? "blue" : String(card.conta) === "3" ? "green" : "yellow" };
    if (o.includes("ecom"))    return { label: "ECOMMERCE", scheme: "purple" };
    if (o.includes("taga"))    return { label: "TAGA", scheme: "teal" };
    return { label: (card.origem || "-").toUpperCase(), scheme: "gray" };
};

const ESTAGIO_LABEL = {
    a_resolver: "A Resolver", em_tratativa: "Em Tratativa",
    aguardando_terceiro: "Aguardando Terceiro", resolvido: "Resolvido",
};

// Status da venda e status_taga - MESMAS cores/rótulos/ícones do painel de Vendas (VendaItem).
const STATUS_VENDA_META = {
    confirmada: { label: "CONFIRMADA",  bg: "gray.600",   color: "white", Icon: IoMdCart },
    enviar:     { label: "PARA ENVIAR", bg: "yellow.200", color: "black", Icon: GoPackage },
    transito:   { label: "EM TRÂNSITO", bg: "blue.500",   color: "white", Icon: MdLocalShipping },
    concluida:  { label: "CONCLUÍDA",   bg: "green.500",  color: "white", Icon: FaCheckCircle },
    cancelada:  { label: "CANCELADA",   bg: "red.500",    color: "white", Icon: MdCancel },
};
const STATUS_TAGA_META = {
    1: { label: "CONFIRMADA", bg: "green.300",  color: "white" },
    2: { label: "CANCELADA",  bg: "red.300",    color: "white" },
    3: { label: "PENDENTE",   bg: "yellow.300", color: "black" },
};
// Opções para as ações de alterar (mesmos valores das telas de Vendas).
const STATUS_VENDA_OPCOES = [
    { v: "confirmada", label: "Confirmada" }, { v: "enviar", label: "Para enviar" },
    { v: "transito", label: "Em trânsito" }, { v: "concluida", label: "Concluída" }, { v: "cancelada", label: "Cancelada" },
];
const STATUS_TAGA_OPCOES = [
    { v: 1, label: "Confirmada" }, { v: 2, label: "Cancelada" }, { v: 3, label: "Pendente" },
];
const MARCADORES = [
    { v: 0, label: "Sem marcador" }, { v: 1, label: "Furo de Estoque" }, { v: 2, label: "Venda com Pendências" },
    { v: 3, label: "Problema no Envio" }, { v: 4, label: "Erro de Precificação" }, { v: 5, label: "Outros Problemas" }, { v: 6, label: "Produto Diferente" },
];

// Opções de ordenação (aplicadas client-side em cada coluna).
const ORDENACOES = [
    { key: "pior_saude",      label: "Pior saúde" },
    { key: "melhor_saude",    label: "Melhor saúde" },
    { key: "mais_atrasadas",  label: "Mais atrasadas" },
    { key: "menos_atrasadas", label: "Menos atrasadas" },
    { key: "maior_valor",     label: "Maior valor" },
    { key: "menor_valor",     label: "Menor valor" },
    { key: "mais_recente",    label: "Venda mais recente" },
    { key: "mais_antiga",     label: "Venda mais antiga" },
];
const SORTS = {
    pior_saude:      (a, b) => (a.saude ?? 101) - (b.saude ?? 101),
    melhor_saude:    (a, b) => (b.saude ?? -1) - (a.saude ?? -1),
    mais_atrasadas:  (a, b) => (b.dias_atraso ?? -1) - (a.dias_atraso ?? -1),
    menos_atrasadas: (a, b) => (a.dias_atraso ?? Infinity) - (b.dias_atraso ?? Infinity),
    maior_valor:     (a, b) => (b.valor_total || 0) - (a.valor_total || 0),
    menor_valor:     (a, b) => (a.valor_total || 0) - (b.valor_total || 0),
    mais_recente:    (a, b) => String(b.data_venda || "").localeCompare(String(a.data_venda || "")),
    mais_antiga:     (a, b) => String(a.data_venda || "").localeCompare(String(b.data_venda || "")),
};

const problemasTexto = (card) => (card.problemas || []).map((p) => p.label).join(", ");

// Resumo formatado da pendência para colar no WhatsApp.
const textoPendencia = (card) => {
    const origem = origemInfo(card);
    const linhas = [
        `🚨 Venda #${card.id_origem || card.venda_id} (${origem.label})`,
        `Produto: ${card.produto?.nome || "-"}`,
    ];
    if (card.produto?.codigo_global) linhas.push(`Cód: ${card.produto.codigo_global}`);
    if (card.fornecedor) linhas.push(`Fornecedor: ${card.fornecedor}`);
    if (card.cliente) linhas.push(`Cliente: ${card.cliente}`);
    if (card.valor_total > 0) linhas.push(`Valor: ${formatBRL(card.valor_total)}`);
    linhas.push(`Problemas: ${problemasTexto(card) || "-"}`);
    if (card.prazo_envio) {
        const atraso = card.dias_atraso != null ? ` (atrasada ${formatAtraso(card)})` : "";
        linhas.push(`Enviar até: ${formatPrazo(card.prazo_envio)}${atraso}`);
    }
    return linhas.join("\n");
};

const copiarTexto = async (texto, toast, titulo = "Copiado") => {
    try {
        await navigator.clipboard.writeText(texto);
        toast({ title: titulo, status: "success", duration: 1600, position: "top-right", isClosable: true });
    } catch {
        toast({ title: "Falha ao copiar", status: "error", duration: 2000 });
    }
};

// ── Card ──────────────────────────────────────────────────────
const PendenciaCard = memo(({ card, onClick, dragHandleProps, isDragging }) => {
    const toast = useToast();
    const origem = origemInfo(card);
    const stVenda = STATUS_VENDA_META[card.status];
    const stTaga = card.status_taga && card.status_taga !== 1 ? STATUS_TAGA_META[card.status_taga] : null;
    const atraso = card.dias_atraso != null ? atrasoMeta(card.dias_atraso) : null;
    const isResolvida = card.resolvida || card.estagio === "resolvido";
    const estagioDesde = ESTAGIO_DESDE[card.estagio];

    return (
        <Box
            onClick={() => onClick(card)}
            {...dragHandleProps}
            bg="white"
            borderRadius="lg"
            boxShadow={isDragging ? "2xl" : "md"}
            borderWidth="1px"
            borderColor={isDragging ? ACCENT : "gray.200"}
            p={2.5}
            cursor="pointer"
            transition="all 0.15s"
            _hover={{ boxShadow: "lg", transform: "translateY(-2px)", borderColor: "orange.300" }}
        >
            <VStack align="stretch" spacing={1.5}>
                {/* Header */}
                <HStack justify="space-between" spacing={1}>
                    <Tooltip label="Copiar ID" hasArrow openDelay={400}>
                        <Text
                            fontSize="11px" fontWeight="bold" color="gray.700" noOfLines={1}
                            cursor="pointer" _hover={{ color: "orange.600" }}
                            onClick={(e) => { e.stopPropagation(); copiarTexto(String(card.id_origem || card.venda_id), toast, "ID copiado"); }}
                        >
                            #{card.id_origem || card.venda_id}
                        </Text>
                    </Tooltip>
                    <HStack spacing={1}>
                        {card.saude != null && (
                            <Tooltip label={`Saúde da resolução: ${card.saude}% (${saudeMeta(card.saude).label})`} hasArrow>
                                <Box display="inline-flex">
                                    <CircularProgress value={card.saude} size="26px" thickness="14px" color={`${saudeMeta(card.saude).scheme}.400`} trackColor="gray.100">
                                        <CircularProgressLabel fontSize="8px" fontWeight="bold" color={`${saudeMeta(card.saude).scheme}.700`}>{card.saude}</CircularProgressLabel>
                                    </CircularProgress>
                                </Box>
                            </Tooltip>
                        )}
                        {card.observacoes_count > 0 && (
                            <Tooltip label={`${card.observacoes_count} observação(ões) nesta venda`} hasArrow>
                                <Badge colorScheme="orange" borderRadius="full" display="inline-flex" alignItems="center" gap="2px" fontSize="9px" px={1.5}>
                                    <Icon as={FaCommentDots} boxSize="9px" /> {card.observacoes_count}
                                </Badge>
                            </Tooltip>
                        )}
                        <Badge colorScheme={origem.scheme} fontSize="9px" px={1.5} borderRadius="md" variant="solid">
                            {origem.label}
                        </Badge>
                        <Tooltip label="Copiar resumo p/ WhatsApp" hasArrow>
                            <IconButton
                                aria-label="Copiar resumo" icon={<FaRegCopy />} size="xs" variant="ghost"
                                colorScheme="gray" minW="20px" h="20px"
                                onClick={(e) => { e.stopPropagation(); copiarTexto(textoPendencia(card), toast, "Resumo copiado"); }}
                            />
                        </Tooltip>
                    </HStack>
                </HStack>

                {/* Status da venda + status TAGA (padrão do painel de Vendas) */}
                <HStack spacing={1} flexWrap="wrap">
                    {stVenda && (
                        <Badge bg={stVenda.bg} color={stVenda.color} fontSize="8px" px={1.5} py={0.5} borderRadius="sm"
                            display="inline-flex" alignItems="center" gap="3px">
                            <Icon as={stVenda.Icon} boxSize="9px" /> {stVenda.label}
                        </Badge>
                    )}
                    {stTaga && (
                        <Tooltip label="Status interno TAGA" hasArrow>
                            <Badge bg={stTaga.bg} color={stTaga.color} fontSize="8px" px={1.5} py={0.5} borderRadius="sm">
                                TAGA: {stTaga.label}
                            </Badge>
                        </Tooltip>
                    )}
                </HStack>

                <Divider borderColor="gray.200" />

                {/* Produto */}
                <HStack spacing={2} align="flex-start">
                    <Box w="42px" h="42px" flexShrink={0}>
                        <ProdutoImagem
                            produto={{ produto_id: card.produto?.produto_id, nome: card.produto?.nome }}
                            origem={null}
                            width="42px"
                            height="42px"
                        />
                    </Box>
                    <VStack align="stretch" spacing={0.5} flex={1} minW={0}>
                        <Tooltip label={card.produto?.nome} openDelay={400}>
                            <Text fontSize="11px" fontWeight="semibold" color="gray.800" noOfLines={2} lineHeight="1.3">
                                {card.produto?.nome || "(produto)"}
                            </Text>
                        </Tooltip>
                        <HStack spacing={1.5} align="center">
                            {card.fornecedor && (
                                <Tooltip label="Fornecedor" hasArrow>
                                    <Badge colorScheme="cyan" fontSize="8px" px={1.5} borderRadius="sm" variant="solid" textTransform="none">
                                        {card.fornecedor}
                                    </Badge>
                                </Tooltip>
                            )}
                            {card.produto?.codigo_global && (
                                <Text fontSize="9px" color="gray.500" noOfLines={1}>{card.produto.codigo_global}</Text>
                            )}
                            {card.qtd_itens > 1 && (
                                <Text fontSize="9px" color="gray.500" noOfLines={1} flexShrink={0}>· {card.qtd_itens} itens</Text>
                            )}
                        </HStack>
                    </VStack>
                </HStack>

                {/* Problemas (badges) */}
                <Wrap spacing={1}>
                    {(card.problemas || []).map((p) => (
                        <WrapItem key={p.code}>
                            <Badge colorScheme={p.cor} fontSize="9px" px={1.5} py={0.5} borderRadius="md" variant="subtle" textTransform="none">
                                {p.label}
                            </Badge>
                        </WrapItem>
                    ))}
                </Wrap>

                <Divider borderColor="gray.100" />

                {!isResolvida && estagioDesde && card.estagio_at && (
                    <Tooltip
                        label={`${estagioDesde.texto} ${formatPrazoLongo(card.estagio_at)} (há ${tempoDesde(card.estagio_at)})`}
                        hasArrow
                    >
                        <Tag size="sm" colorScheme={estagioDesde.scheme} variant="subtle" borderRadius="full" alignSelf="flex-start">
                            <Icon as={FaClock} boxSize="8px" mr={1} />
                            <TagLabel fontSize="9px" whiteSpace="nowrap" overflow="visible">
                                {estagioDesde.texto} {formatPrazo(card.estagio_at)} ({tempoDesde(card.estagio_at)})
                            </TagLabel>
                        </Tag>
                    </Tooltip>
                )}

                {isResolvida && card.resolvida_at && (
                    <HStack spacing={1.5} flexWrap="wrap" rowGap={1}>
                        <Tooltip label={`Resolvida em ${formatPrazoLongo(card.resolvida_at)}`} hasArrow>
                            <Tag size="sm" colorScheme="green" variant="subtle" borderRadius="full" flexShrink={0}>
                                <Icon as={FaCheckCircle} boxSize="8px" mr={1} />
                                <TagLabel fontSize="9px" whiteSpace="nowrap" overflow="visible">
                                    resolvida {formatPrazo(card.resolvida_at)}
                                </TagLabel>
                            </Tag>
                        </Tooltip>
                        {card.tempo_resolucao?.label && (
                            <Tooltip
                                label={`Ficou aberta ${card.tempo_resolucao.label}${card.primeira_deteccao_at ? `, detectada em ${formatPrazoLongo(card.primeira_deteccao_at)}` : ""}`}
                                hasArrow
                            >
                                <Tag size="sm" colorScheme="purple" variant="subtle" borderRadius="full" flexShrink={0}>
                                    <Icon as={FaClock} boxSize="8px" mr={1} />
                                    <TagLabel fontSize="9px" whiteSpace="nowrap" overflow="visible">
                                        aberta {card.tempo_resolucao.label}
                                    </TagLabel>
                                </Tag>
                            </Tooltip>
                        )}
                    </HStack>
                )}

                {/* Footer */}
                <HStack justify="space-between" spacing={1} align="center" flexWrap="wrap" rowGap={1}>
                    <HStack spacing={1.5} flexWrap="wrap" rowGap={1}>
                        {card.prazo_envio && (
                            <Tooltip label={`Prazo de envio: ${formatPrazoLongo(card.prazo_envio)}`} hasArrow>
                                <Tag size="sm" colorScheme={card.dias_atraso != null ? "gray" : "blue"} variant="subtle" borderRadius="full" flexShrink={0}>
                                    <Icon as={FaTruck} boxSize="8px" mr={1} />
                                    <TagLabel fontSize="9px" whiteSpace="nowrap" overflow="visible">Enviar até {formatPrazo(card.prazo_envio)}</TagLabel>
                                </Tag>
                            </Tooltip>
                        )}
                        {atraso && (
                            <Tooltip label={`Atrasada há ${formatAtraso(card)}`} hasArrow>
                                <Tag size="sm" bg={atraso.bg} color={atraso.color} borderRadius="full" flexShrink={0}>
                                    <Icon as={FaClock} boxSize="8px" mr={1} />
                                    <TagLabel fontSize="9px" whiteSpace="nowrap" overflow="visible">atrasada {formatAtraso(card)}</TagLabel>
                                </Tag>
                            </Tooltip>
                        )}
                    </HStack>
                    {card.valor_total > 0 && (
                        <Text fontSize="11px" fontWeight="bold" color="gray.700" flexShrink={0}>
                            {formatBRL(card.valor_total)}
                        </Text>
                    )}
                </HStack>

                {card.cliente && (
                    <HStack spacing={1} minW={0} color="gray.500">
                        <Icon as={FaUser} boxSize="8px" />
                        <Text fontSize="10px" noOfLines={1}>{card.cliente}</Text>
                    </HStack>
                )}

                {/* Referências ML: id_origem + pack_id_ml (copiáveis) */}
                {origem.label.startsWith("ML") && (card.id_origem || card.pack_id_ml) && (
                    <>
                        <Divider borderColor="gray.100" />
                        <HStack spacing={2} divider={<Text color="gray.300" fontSize="9px">|</Text>} flexWrap="wrap">
                            {card.id_origem && (
                                <Tooltip label="Copiar ID Origem" hasArrow openDelay={400}>
                                    <Text fontSize="9px" color="gray.500" cursor="pointer" _hover={{ color: "orange.600" }}
                                        onClick={(e) => { e.stopPropagation(); copiarTexto(String(card.id_origem), toast, "ID Origem copiado"); }}>
                                        Origem: {card.id_origem}
                                    </Text>
                                </Tooltip>
                            )}
                            {card.pack_id_ml && (
                                <Tooltip label="Copiar Pack ID" hasArrow openDelay={400}>
                                    <Text fontSize="9px" color="gray.500" cursor="pointer" _hover={{ color: "orange.600" }}
                                        onClick={(e) => { e.stopPropagation(); copiarTexto(String(card.pack_id_ml), toast, "Pack ID copiado"); }}>
                                        Pack: {card.pack_id_ml}
                                    </Text>
                                </Tooltip>
                            )}
                        </HStack>
                    </>
                )}

            </VStack>
        </Box>
    );
});

// ── Modal de detalhes / tratativa ─────────────────────────────
// ── Helpers do modal ──────────────────────────────────────────
const CONF_STATUS = {
    0: { label: "Não conferido", scheme: "gray" },
    1: { label: "Conferido", scheme: "green" },
    2: { label: "Problema", scheme: "red" },
    3: { label: "Embalado", scheme: "blue" },
    4: { label: "Enviado", scheme: "purple" },
    5: { label: "Liberado c/ ressalva", scheme: "yellow" },
};
const statusLcScheme = (s) => ({
    pendente: "yellow", em_andamento: "blue", enviado: "blue", respondido: "cyan",
    aprovado: "green", parcialmente_aprovado: "orange", rejeitado: "red",
    finalizado: "green", cancelado: "gray", sem_estoque: "red", erro: "red",
}[s] || "gray");
const fmtData = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : null);

const Secao = ({ icon, title, children, action }) => (
    <Box bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.200" p={{ base: 3, md: 4 }} boxShadow="xs">
        <HStack justify="space-between" mb={3}>
            <HStack spacing={2}>
                {icon && <Icon as={icon} color="orange.500" boxSize="14px" />}
                <Text fontWeight="bold" fontSize="sm" color="gray.700">{title}</Text>
            </HStack>
            {action}
        </HStack>
        {children}
    </Box>
);

const Campo = ({ label, children, onCopy }) => (
    <Box minW={0}>
        <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="bold" letterSpacing="0.03em">{label}</Text>
        <Text
            fontSize="sm" color="gray.800" wordBreak="break-word"
            cursor={onCopy ? "pointer" : "default"} _hover={onCopy ? { color: "orange.600" } : undefined}
            onClick={onCopy}
        >
            {(children ?? "") !== "" && children != null ? children : <Text as="span" color="gray.300">-</Text>}
        </Text>
    </Box>
);

const VazioAba = ({ icon, texto }) => (
    <Flex direction="column" align="center" justify="center" py={10} color="gray.400">
        <Icon as={icon} boxSize={8} mb={2} opacity={0.5} />
        <Text fontSize="sm">{texto}</Text>
    </Flex>
);

const InfoTip = ({ texto }) => (
    <Tooltip label={texto} hasArrow placement="top" maxW="260px">
        <Box as="span" display="inline-flex" ml={1} color="gray.400" cursor="help" verticalAlign="middle">
            <Icon as={FaQuestionCircle} boxSize="10px" />
        </Box>
    </Tooltip>
);

const DetalhesModal = ({ card, isOpen, onClose, onEstagio, onAlterarStatus, onAlterarStatusTaga, onAlterarMarcador }) => {
    const toast = useToast();
    const [det, setDet] = useState(null);
    const [loadingDet, setLoadingDet] = useState(false);
    const [chatOpen, setChatOpen] = useState(false);
    const [confirmacao, setConfirmacao] = useState(null); // { titulo, mensagem, onConfirm }
    const cancelRef = useRef();

    const carregarDet = useCallback((silent = false) => {
        if (!card) return;
        if (!silent) setLoadingDet(true);
        getDetalhesPendencia(card.venda_id).then((res) => {
            if (res) setDet(res);
            setLoadingDet(false);
        });
    }, [card]);

    useEffect(() => {
        if (!isOpen || !card) return;
        setDet(null);
        carregarDet();
    }, [isOpen, card, carregarDet]);

    if (!card) return null;
    const origem = origemInfo(card);
    const isResolvida = card.resolvida || card.estagio === "resolvido";
    const atrasoCard = card.dias_atraso != null ? atrasoMeta(card.dias_atraso) : null;
    const isML = (card.origem || "").toLowerCase().includes("mercado");

    const copiar = (v, label) => copiarTexto(String(v), toast, `${label} copiado`);
    const pedirConfirmacao = (label, valorLabel, aplicar) => setConfirmacao({
        titulo: `Alterar ${label}`,
        mensagem: `Confirma alterar o ${label} desta venda para "${valorLabel}"? A alteração é aplicada direto na venda.`,
        onConfirm: aplicar,
    });
    const Loader = <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>;

    // Estilo comum das abas (scroll horizontal no mobile)
    const tabProps = { fontSize: { base: "xs", md: "sm" }, fontWeight: "semibold", whiteSpace: "nowrap", px: { base: 3, md: 4 } };

    return (
        <>
        <Modal isOpen={isOpen} onClose={onClose} size={{ base: "full", md: "3xl", lg: "5xl" }} scrollBehavior="inside" isCentered>
            <ModalOverlay bg="blackAlpha.600" />
            <ModalContent
                h={{ base: "100dvh" }}
                minH={{ base: "unset" }}
                maxH={{ base: "100dvh", md: "92vh" }}
                m={{ base: 0 }}
                borderRadius={{ base: 0, md: "xl" }}
                overflow="hidden"
            >
                {/* Cabeçalho */}
                <ModalHeader bg="gray.50" borderBottomWidth="1px" borderColor="gray.200" py={3}>
                    <HStack spacing={2} flexWrap="wrap" pr={8} align="center">
                        <Heading size="md" color="gray.800">Venda #{card.id_origem || card.venda_id}</Heading>
                        <Badge colorScheme={origem.scheme} variant="solid">{origem.label}</Badge>
                        {STATUS_VENDA_META[card.status] && (
                            <Badge bg={STATUS_VENDA_META[card.status].bg} color={STATUS_VENDA_META[card.status].color} display="inline-flex" alignItems="center" gap="4px">
                                <Icon as={STATUS_VENDA_META[card.status].Icon} boxSize="11px" /> {STATUS_VENDA_META[card.status].label}
                            </Badge>
                        )}
                        {card.status_taga && card.status_taga !== 1 && STATUS_TAGA_META[card.status_taga] && (
                            <Badge bg={STATUS_TAGA_META[card.status_taga].bg} color={STATUS_TAGA_META[card.status_taga].color}>
                                TAGA: {STATUS_TAGA_META[card.status_taga].label}
                            </Badge>
                        )}
                        <Button size="xs" variant="outline" colorScheme="orange" leftIcon={<FaRegCopy />}
                            onClick={() => copiarTexto(textoPendencia(card), toast, "Resumo copiado")}>
                            Copiar
                        </Button>
                    </HStack>
                </ModalHeader>
                <ModalCloseButton top={3} />

                <ModalBody p={0} bg="gray.50" overflowY="auto" overflowX="hidden" sx={{ WebkitOverflowScrolling: "touch" }}>
                    <Tabs colorScheme="orange" variant="line" isLazy>
                        <TabList
                            overflowX="auto" overflowY="hidden" bg="white" px={{ base: 1, md: 2 }}
                            position="sticky" top={0} zIndex={2}
                            css={{ scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
                        >
                            <Tab {...tabProps}><Icon as={FaExclamationTriangle} mr={2} boxSize="12px" />Resumo</Tab>
                            <Tab {...tabProps}>
                                <Icon as={FaRegStickyNote} mr={2} boxSize="12px" />Observações
                                {det?.observacoes?.length > 0 && <Badge ml={2} colorScheme="orange" borderRadius="full">{det.observacoes.length}</Badge>}
                            </Tab>
                            <Tab {...tabProps}><Icon as={FaShoppingCart} mr={2} boxSize="12px" />Pedido</Tab>
                            <Tab {...tabProps}><Icon as={FaUser} mr={2} boxSize="12px" />Cliente</Tab>
                            <Tab {...tabProps}><Icon as={FaFileInvoice} mr={2} boxSize="12px" />NFe / Etiqueta</Tab>
                            <Tab {...tabProps}><Icon as={FaClipboardCheck} mr={2} boxSize="12px" />Conferência</Tab>
                            <Tab {...tabProps}><Icon as={FaBoxes} mr={2} boxSize="12px" />Lista de compra</Tab>
                            <Tab {...tabProps}><Icon as={FaHistory} mr={2} boxSize="12px" />Histórico</Tab>
                        </TabList>

                        <TabPanels>
                            {/* ---------- RESUMO ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                <VStack align="stretch" spacing={3}>
                                    {card.saude != null && (
                                        <Secao title="Saúde da resolução">
                                            <HStack spacing={4} align="center">
                                                <CircularProgress value={card.saude} color={`${saudeMeta(card.saude).scheme}.400`} size="66px" thickness="10px" trackColor="gray.100">
                                                    <CircularProgressLabel fontWeight="bold" fontSize="md">{card.saude}%</CircularProgressLabel>
                                                </CircularProgress>
                                                <VStack align="stretch" flex={1} spacing={1} minW={0}>
                                                    <Badge colorScheme={saudeMeta(card.saude).scheme} alignSelf="flex-start" fontSize="sm" px={2} py={0.5}>{saudeMeta(card.saude).label}</Badge>
                                                    <Text fontSize="xs" color="gray.500">Piora conforme o atraso, a quantidade de problemas e o tempo sem resolução. Quanto menor, mais urgente.</Text>
                                                </VStack>
                                            </HStack>
                                        </Secao>
                                    )}
                                    <Secao icon={FaBoxOpen} title="Produto">
                                        <HStack align="flex-start" spacing={3}>
                                            <Box w={{ base: "56px", md: "72px" }} h={{ base: "56px", md: "72px" }} flexShrink={0}>
                                                <ProdutoImagem produto={{ produto_id: card.produto?.produto_id, nome: card.produto?.nome }} origem={null} width="100%" height="100%" />
                                            </Box>
                                            <VStack align="stretch" spacing={1} flex={1} minW={0}>
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">{card.produto?.nome}</Text>
                                                <HStack spacing={2} flexWrap="wrap">
                                                    {card.fornecedor && <Badge colorScheme="cyan" variant="solid" textTransform="none">{card.fornecedor}</Badge>}
                                                    {card.produto?.codigo_global && <Text fontSize="xs" color="gray.500">Global: {card.produto.codigo_global}</Text>}
                                                    {card.produto?.codigo_fornecedor && <Text fontSize="xs" color="gray.500">Forn.: {card.produto.codigo_fornecedor}</Text>}
                                                </HStack>
                                                {card.valor_total > 0 && <Text fontSize="lg" fontWeight="bold" color="green.600">{formatBRL(card.valor_total)}</Text>}
                                            </VStack>
                                        </HStack>
                                    </Secao>

                                    <Secao icon={FaExclamationTriangle} title="Problemas detectados">
                                        <Wrap spacing={1.5}>
                                            {(card.problemas || []).map((p) => (
                                                <WrapItem key={p.code}>
                                                    <Badge colorScheme={p.cor} px={2} py={1} borderRadius="md" textTransform="none">{p.label}</Badge>
                                                </WrapItem>
                                            ))}
                                            {(!card.problemas || card.problemas.length === 0) && <Text fontSize="xs" color="gray.400">Sem problemas ativos.</Text>}
                                        </Wrap>
                                        <HStack mt={3} spacing={2} flexWrap="wrap">
                                            {card.prazo_envio && (
                                                <Tag size="sm" colorScheme="gray" variant="subtle" borderRadius="full">
                                                    <Icon as={FaTruck} boxSize="9px" mr={1} /> Enviar até {formatPrazo(card.prazo_envio)}
                                                </Tag>
                                            )}
                                            {atrasoCard && (
                                                <Tag size="sm" bg={atrasoCard.bg} color={atrasoCard.color} borderRadius="full">
                                                    <Icon as={FaClock} boxSize="9px" mr={1} /> atrasada {formatAtraso(card)}
                                                </Tag>
                                            )}
                                        </HStack>
                                    </Secao>

                                    {isResolvida ? (
                                        <HStack spacing={2} bg="green.50" borderRadius="lg" p={3} borderLeft="4px solid" borderColor="green.400">
                                            <Icon as={FaCheckCircle} color="green.500" boxSize={5} flexShrink={0} />
                                            <Text fontSize="sm" color="gray.700">
                                                Venda <b>resolvida</b>, saiu do fluxo de pendência automaticamente.
                                                {card.tempo_resolucao ? <> Tempo de resolução: <b>{card.tempo_resolucao.label}</b>.</> : ""}
                                            </Text>
                                        </HStack>
                                    ) : (
                                        <Secao icon={FaSyncAlt} title="Mover para">
                                            <Wrap spacing={2}>
                                                {COLUNAS.filter((c) => c.key !== "resolvido").map((c) => (
                                                    <WrapItem key={c.key}>
                                                        <Button size="sm" variant={card.estagio === c.key ? "solid" : "outline"} colorScheme={c.scheme}
                                                            isDisabled={card.estagio === c.key} onClick={() => onEstagio(card, c.key)}>
                                                            {c.title}
                                                        </Button>
                                                    </WrapItem>
                                                ))}
                                            </Wrap>
                                        </Secao>
                                    )}

                                    {/* Ações da venda */}
                                    {isML && (
                                        <Button colorScheme="blue" variant="outline" leftIcon={<Icon as={FaCommentDots} />} onClick={() => setChatOpen(true)} alignSelf="flex-start">
                                            Abrir chat do Mercado Livre
                                        </Button>
                                    )}
                                    <Secao title="Alterar dados da venda">
                                        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                                            <Box>
                                                <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="bold" mb={1}>
                                                    Status da venda<InfoTip texto="Situação do pedido no fluxo de envio: Confirmada, Para enviar, Em trânsito, Concluída ou Cancelada. Vendas do Mercado Livre seguem o status do marketplace e não são editáveis aqui." />
                                                </Text>
                                                <Select size="sm" bg="white" value={card.status || ""} isDisabled={isML} title={isML ? "Vendas do Mercado Livre seguem o marketplace" : undefined} onChange={(e) => {
                                                    const v = e.target.value;
                                                    pedirConfirmacao("Status da venda", STATUS_VENDA_OPCOES.find((o) => o.v === v)?.label || v, () => onAlterarStatus(card.venda_id, v));
                                                }}>
                                                    {STATUS_VENDA_OPCOES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                                                </Select>
                                                {isML && <Text fontSize="9px" color="gray.400" mt={0.5}>ML segue o marketplace</Text>}
                                            </Box>
                                            <Box>
                                                <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="bold" mb={1}>
                                                    Status TAGA<InfoTip texto="Status interno da TAGA para a venda: Confirmada, Cancelada ou Pendente (aguardando definição)." />
                                                </Text>
                                                <Select size="sm" bg="white" value={String(card.status_taga ?? "")} onChange={(e) => {
                                                    const v = e.target.value;
                                                    pedirConfirmacao("Status TAGA", STATUS_TAGA_OPCOES.find((o) => String(o.v) === v)?.label || v, () => onAlterarStatusTaga(card.venda_id, v));
                                                }}>
                                                    {STATUS_TAGA_OPCOES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                                                </Select>
                                            </Box>
                                            <Box>
                                                <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="bold" mb={1}>
                                                    Marcador<InfoTip texto="Marcador manual de problema da venda: furo de estoque, venda com pendências, problema no envio, erro de precificação, produto diferente, etc." />
                                                </Text>
                                                <Select size="sm" bg="white" value={String(card.marcador ?? 0)} onChange={(e) => {
                                                    const v = e.target.value;
                                                    pedirConfirmacao("Marcador", MARCADORES.find((o) => String(o.v) === v)?.label || v, () => onAlterarMarcador(card.venda_id, v));
                                                }}>
                                                    {MARCADORES.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                                                </Select>
                                            </Box>
                                        </SimpleGrid>
                                    </Secao>
                                </VStack>
                            </TabPanel>

                            {/* ---------- OBSERVAÇÕES (componente padrão do sistema) ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet && !det ? Loader : (
                                    <ObservacoesVenda
                                        observacoes={det?.observacoes || []}
                                        vendaId={card.venda_id}
                                        onAdicionado={() => carregarDet(true)}
                                    />
                                )}
                            </TabPanel>

                            {/* ---------- PEDIDO ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet || !det ? Loader : (
                                    <VStack align="stretch" spacing={3}>
                                        <Secao icon={FaShoppingCart} title="Dados do pedido">
                                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                                <Campo label="ID Origem" onCopy={det.pedido.id_origem ? () => copiar(det.pedido.id_origem, "ID Origem") : undefined}>{det.pedido.id_origem}</Campo>
                                                <Campo label="Pack ID" onCopy={det.pedido.pack_id_ml ? () => copiar(det.pedido.pack_id_ml, "Pack ID") : undefined}>{det.pedido.pack_id_ml}</Campo>
                                                <Campo label="Origem">{det.pedido.origem}</Campo>
                                                <Campo label="Data da venda">{det.pedido.data_venda ? new Date(det.pedido.data_venda).toLocaleDateString("pt-BR") : ""}</Campo>
                                                <Campo label="Valor total"><Text as="span" fontWeight="bold" color="green.600">{formatBRL(det.pedido.valor_total)}</Text></Campo>
                                                <Campo label="Itens">{det.pedido.itens.length}</Campo>
                                                {det.pedido.vendedor && <Campo label="Vendedor"><Text as="span" fontWeight="semibold" color="orange.600">{det.pedido.vendedor}</Text></Campo>}
                                            </SimpleGrid>
                                        </Secao>
                                        {det.pedido.itens.map((it, i) => (
                                            <Secao key={i} icon={FaBoxOpen} title={`Item ${i + 1}`} action={it.fornecedor ? <Badge colorScheme="cyan" variant="solid" textTransform="none">{it.fornecedor}</Badge> : null}>
                                                <HStack align="flex-start" spacing={3}>
                                                    <Box w="56px" h="56px" flexShrink={0}>
                                                        <ProdutoImagem produto={{ produto_id: it.produto_id, nome: it.nome }} origem={null} width="100%" height="100%" />
                                                    </Box>
                                                    <VStack align="stretch" spacing={2} flex={1} minW={0}>
                                                        <Text fontSize="sm" fontWeight="semibold">{it.nome}</Text>
                                                        <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                                                            <Campo label="SKU">{it.produto_id}</Campo>
                                                            <Campo label="Cód. Global">{it.codigo_global}</Campo>
                                                            <Campo label="Cód. Fornecedor">{it.codigo_fornecedor}</Campo>
                                                            <Campo label="Cód. Fabricante">{it.codigo_fabricante}</Campo>
                                                            <Campo label="Cód. Barra">{it.codigo_barra}</Campo>
                                                            <Campo label="Marca">{it.marca}</Campo>
                                                            <Campo label="Localização">{it.localizacao}</Campo>
                                                            <Campo label="Quantidade">{it.quantidade}</Campo>
                                                            <Campo label="Valor unit.">{formatBRL(it.valor_unitario)}</Campo>
                                                        </SimpleGrid>
                                                        {it.estoque_taga && (
                                                            <HStack bg="purple.50" borderRadius="md" p={2} spacing={3} borderLeft="3px solid" borderColor="purple.400" flexWrap="wrap">
                                                                <HStack spacing={1} color="purple.700"><Icon as={FaWarehouse} boxSize="12px" /><Text fontSize="xs" fontWeight="bold">ESTOQUE TAGA</Text></HStack>
                                                                <Text fontSize="xs" color="gray.700">Qtd TAGA: <b>{it.qtd_taga ?? "-"}</b></Text>
                                                                <Text fontSize="xs" color="gray.700">Localização: <b>{it.localizacao || "-"}</b></Text>
                                                            </HStack>
                                                        )}
                                                        <HStack justify="space-between">
                                                            {it.furo_taga && <Badge colorScheme="gray">Peça não localizada</Badge>}
                                                            <Text ml="auto" fontWeight="bold" color="green.600">{formatBRL(it.total)}</Text>
                                                        </HStack>
                                                    </VStack>
                                                </HStack>
                                            </Secao>
                                        ))}
                                    </VStack>
                                )}
                            </TabPanel>

                            {/* ---------- CLIENTE ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet || !det ? Loader : (
                                    <VStack align="stretch" spacing={3}>
                                        <Secao icon={FaUser} title="Cliente">
                                            {det.cliente ? (
                                                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                                                    <Campo label="Nome">{det.cliente.nome}</Campo>
                                                    <Campo label="Documento" onCopy={det.cliente.documento ? () => copiar(det.cliente.documento, "Documento") : undefined}>{det.cliente.documento}</Campo>
                                                    <Campo label="Tipo">{det.cliente.tipo}</Campo>
                                                    <Campo label="E-mail">{det.cliente.email}</Campo>
                                                    <Campo label="Telefone">{det.cliente.telefone}</Campo>
                                                    <Campo label="Apelido (ML)">{det.cliente.nickname}</Campo>
                                                </SimpleGrid>
                                            ) : <Text fontSize="sm" color="gray.400">Sem dados de cliente.</Text>}
                                        </Secao>
                                        <Secao icon={FaMapMarkerAlt} title="Endereço de entrega">
                                            {det.endereco ? (
                                                <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                                                    <Campo label="CEP">{det.endereco.cep}</Campo>
                                                    <Campo label="Cidade / UF">{[det.endereco.cidade, det.endereco.uf].filter(Boolean).join(" / ")}</Campo>
                                                    <Campo label="Bairro">{det.endereco.bairro}</Campo>
                                                    <Campo label="Logradouro">{det.endereco.logradouro}</Campo>
                                                    <Campo label="Número">{det.endereco.numero}</Campo>
                                                    <Campo label="Complemento">{det.endereco.complemento}</Campo>
                                                </SimpleGrid>
                                            ) : <Text fontSize="sm" color="gray.400">Sem endereço cadastrado.</Text>}
                                        </Secao>
                                    </VStack>
                                )}
                            </TabPanel>

                            {/* ---------- NFe / ETIQUETA ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet || !det ? Loader : (
                                    <VStack align="stretch" spacing={3}>
                                        <Secao icon={FaFileInvoice} title="Nota fiscal (NFe)"
                                            action={det.nfe ? <Badge colorScheme={det.nfe.autorizada ? "green" : (det.nfe.erro ? "red" : "yellow")}>{det.nfe.autorizada ? "Autorizada" : (det.nfe.erro ? "Erro" : "Pendente")}</Badge> : null}>
                                            {det.nfe ? (
                                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                                                    <Campo label="Número">{det.nfe.numero}</Campo>
                                                    <Campo label="Emissão">{det.nfe.data_emissao ? new Date(det.nfe.data_emissao).toLocaleString("pt-BR") : ""}</Campo>
                                                    <Box gridColumn={{ sm: "span 2" }}>
                                                        <Campo label="Chave" onCopy={det.nfe.chave ? () => copiar(det.nfe.chave, "Chave") : undefined}>{det.nfe.chave}</Campo>
                                                    </Box>
                                                    {det.nfe.erro && <Box gridColumn={{ sm: "span 2" }}><Campo label="Motivo do erro"><Text as="span" color="red.500">{det.nfe.erro}</Text></Campo></Box>}
                                                </SimpleGrid>
                                            ) : <Text fontSize="sm" color="gray.400">NFe ainda não emitida.</Text>}
                                        </Secao>
                                        <Secao icon={FaTruck} title="Envio e etiqueta">
                                            <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                                                <Campo label="Modo de envio">{det.envio.modo}</Campo>
                                                <Campo label="Transportadora">{det.envio.empresa || det.envio.transportadora_comb}</Campo>
                                                <Campo label="Prazo de envio">{det.envio.data_limite ? new Date(det.envio.data_limite).toLocaleString("pt-BR") : ""}</Campo>
                                                <Campo label="Flex ML">{det.envio.flex_ml ? "Sim" : "Não"}</Campo>
                                                <Campo label="Envio externo">{det.envio.envio_externo ? "Sim" : "Não"}</Campo>
                                                <Campo label="Status etiqueta">{det.envio.etiqueta_status}</Campo>
                                                <Campo label="Rastreio" onCopy={det.envio.codigo_rastreio ? () => copiar(det.envio.codigo_rastreio, "Rastreio") : undefined}>{det.envio.codigo_rastreio}</Campo>
                                            </SimpleGrid>
                                        </Secao>
                                    </VStack>
                                )}
                            </TabPanel>

                            {/* ---------- CONFERÊNCIA ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet || !det ? Loader : det.conferencias.length === 0 ? (
                                    <VazioAba icon={FaClipboardCheck} texto="Nenhuma conferência registrada." />
                                ) : (
                                    <VStack align="stretch" spacing={3}>
                                        {det.conferencias.map((c, i) => {
                                            const st = CONF_STATUS[c.status] || CONF_STATUS[0];
                                            return (
                                                <Secao key={i} icon={FaClipboardCheck} title={c.produto || `Conferência ${i + 1}`}
                                                    action={<Badge colorScheme={st.scheme}>{st.label}</Badge>}>
                                                    <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={3}>
                                                        <Campo label="Conferido / Esperado">
                                                            <Text as="span" fontWeight="bold"
                                                                color={(c.unidades_check != null && c.quantidade_esperada != null && c.unidades_check !== c.quantidade_esperada) ? "red.500" : "gray.800"}>
                                                                {c.unidades_check ?? "-"} / {c.quantidade_esperada ?? "-"}
                                                            </Text>
                                                        </Campo>
                                                        <Campo label="Outro fornecedor">{c.outro_fornecedor}</Campo>
                                                        {c.problema_at && <Campo label="Problema em">{new Date(c.problema_at).toLocaleString("pt-BR")}</Campo>}
                                                    </SimpleGrid>
                                                    {c.flags.length > 0 && (
                                                        <Wrap mt={2} spacing={1.5}>
                                                            {c.flags.map((f) => <WrapItem key={f}><Badge colorScheme="red" variant="subtle">{f}</Badge></WrapItem>)}
                                                        </Wrap>
                                                    )}
                                                    {c.observacao && <Text mt={2} fontSize="sm" color="gray.700" bg="gray.50" p={2} borderRadius="md" whiteSpace="pre-wrap">{c.observacao}</Text>}
                                                </Secao>
                                            );
                                        })}
                                    </VStack>
                                )}
                            </TabPanel>

                            {/* ---------- LISTA DE COMPRA ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet || !det ? Loader : det.lista_compra.length === 0 ? (
                                    <VazioAba icon={FaBoxes} texto="Sem lista de compra vinculada a esta venda." />
                                ) : (
                                    <VStack align="stretch" spacing={3}>
                                        {det.lista_compra.map((l, i) => (
                                            <Secao key={i} icon={FaBoxes} title={l.produto || `Item ${i + 1}`}
                                                action={
                                                    <HStack spacing={1} flexWrap="wrap" justify="flex-end">
                                                        {l.fornecedor && <Badge colorScheme="cyan" variant="solid" textTransform="none">{l.fornecedor}</Badge>}
                                                        {l.urgente && <Badge colorScheme="red">Urgente</Badge>}
                                                        {l.tem_furo && <Badge colorScheme="orange">Furo</Badge>}
                                                    </HStack>
                                                }>
                                                {/* Situação geral da lista */}
                                                <Box bg="gray.50" borderRadius="md" p={2.5} mb={3}>
                                                    <HStack mb={2} spacing={2} flexWrap="wrap">
                                                        <Text fontSize="xs" color="gray.500" fontWeight="semibold">Lista #{l.lista_id}</Text>
                                                        {l.lista_status && <Badge colorScheme={statusLcScheme(l.lista_status)} textTransform="none">{l.lista_status}</Badge>}
                                                        {l.lista_status_envio && <Badge colorScheme={statusLcScheme(l.lista_status_envio)} variant="outline" textTransform="none">envio: {l.lista_status_envio}</Badge>}
                                                    </HStack>
                                                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
                                                        <Campo label="Criada">{fmtData(l.data_lista)}</Campo>
                                                        <Campo label="Respondida">{fmtData(l.respondido_em)}</Campo>
                                                        <Campo label="Aprovada">{fmtData(l.aprovado_em)}</Campo>
                                                        <Campo label="Finalizada">{fmtData(l.finalizado_em)}</Campo>
                                                    </SimpleGrid>
                                                    {l.obs_fornecedor && <Text mt={2} fontSize="xs" color="gray.600" fontStyle="italic">"{l.obs_fornecedor}"</Text>}
                                                </Box>

                                                {/* Situação do item */}
                                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                                    <Campo label="Cód. Fornecedor">{l.codigo_fornecedor}</Campo>
                                                    <Campo label="Marca">{l.marca}</Campo>
                                                    <Campo label="Qtd solic. / aprov.">{l.qtd_solicitada} / {l.qtd_aprovada ?? "-"}</Campo>
                                                    <Campo label="Estado">{l.estado ? <Badge colorScheme={statusLcScheme((l.estado || "").toLowerCase())} textTransform="none" fontSize="10px">{l.estado}</Badge> : null}</Campo>
                                                    <Campo label="Resposta">{l.status_resposta}</Campo>
                                                    <Campo label="Aprovação">{l.status_aprovacao}</Campo>
                                                    <Campo label="Preço ref. / máx.">{l.preco_referencia != null ? formatBRL(l.preco_referencia) : "-"}{l.preco_maximo != null ? ` / ${formatBRL(l.preco_maximo)}` : ""}</Campo>
                                                    {l.furo_declarado_em && <Campo label="Furo declarado">{fmtData(l.furo_declarado_em)}{l.furo_aceito_em ? ` (aceito ${fmtData(l.furo_aceito_em)})` : ""}</Campo>}
                                                </SimpleGrid>

                                                {l.respostas.length > 0 && (
                                                    <Box mt={3}>
                                                        <Text fontSize="10px" color="gray.400" textTransform="uppercase" fontWeight="bold" mb={1}>Respostas do fornecedor</Text>
                                                        <VStack align="stretch" spacing={1.5}>
                                                            {l.respostas.map((r, ri) => (
                                                                <Box key={ri} bg="gray.50" borderRadius="md" p={2}>
                                                                    <HStack spacing={3} flexWrap="wrap" fontSize="xs">
                                                                        <Text fontWeight="semibold">{r.codigo || "-"}</Text>
                                                                        {r.marca && <Text color="gray.500">{r.marca}</Text>}
                                                                        <Text>Qtd: {r.qtd ?? "-"}</Text>
                                                                        <Text fontWeight="semibold" color="green.600">{r.preco != null ? formatBRL(r.preco) : "-"}</Text>
                                                                        {r.prazo_entrega != null && <Text color="gray.500">{r.prazo_entrega}d entrega</Text>}
                                                                        {r.condicao_pgto && <Text color="gray.500">{r.condicao_pgto}</Text>}
                                                                    </HStack>
                                                                    <Wrap mt={1.5} spacing={1}>
                                                                        {r.furo && <Badge colorScheme="orange" fontSize="9px">Furo</Badge>}
                                                                        {r.marca_diferente && <Badge colorScheme="yellow" fontSize="9px">Marca diferente</Badge>}
                                                                        {r.similar && <Badge colorScheme="purple" fontSize="9px">Similar</Badge>}
                                                                        {r.problema_imagem && <Badge colorScheme="red" fontSize="9px">Problema imagem</Badge>}
                                                                        {r.status && <Badge colorScheme={r.status === "aprovado" ? "green" : (r.status === "rejeitado" ? "red" : "gray")} fontSize="9px" textTransform="none">{r.status}</Badge>}
                                                                    </Wrap>
                                                                </Box>
                                                            ))}
                                                        </VStack>
                                                    </Box>
                                                )}
                                            </Secao>
                                        ))}
                                    </VStack>
                                )}
                            </TabPanel>

                            {/* ---------- HISTÓRICO ---------- */}
                            <TabPanel px={{ base: 3, md: 4 }} py={4}>
                                {loadingDet || !det ? Loader : (
                                    <VStack align="stretch" spacing={3}>
                                        <Secao icon={FaClock} title="Linha do tempo do status">
                                            {det.historico.status_timeline.length === 0 ? (
                                                <Text fontSize="sm" color="gray.400">Sem transições registradas.</Text>
                                            ) : (
                                                <VStack align="stretch" spacing={0}>
                                                    {det.historico.status_timeline.map((s, i) => (
                                                        <HStack key={i} align="flex-start" spacing={3}>
                                                            <VStack spacing={0} align="center" alignSelf="stretch">
                                                                <Box w="11px" h="11px" borderRadius="full" bg="orange.400" mt={1} flexShrink={0} />
                                                                {i < det.historico.status_timeline.length - 1 && <Box w="2px" flex={1} bg="gray.200" />}
                                                            </VStack>
                                                            <Box pb={i < det.historico.status_timeline.length - 1 ? 4 : 0}>
                                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">{s.label}</Text>
                                                                <Text fontSize="xs" color="gray.500">{new Date(s.data).toLocaleString("pt-BR")}</Text>
                                                            </Box>
                                                        </HStack>
                                                    ))}
                                                </VStack>
                                            )}
                                        </Secao>
                                        <Secao icon={FaHistory} title="Movimentações do pedido">
                                            {det.historico.logs.length === 0 ? (
                                                <VazioAba icon={FaHistory} texto="Sem movimentações registradas." />
                                            ) : (
                                                <VStack align="stretch" spacing={2}>
                                                    {det.historico.logs.map((lg, i) => (
                                                        <Box key={i} bg="gray.50" borderRadius="md" p={2.5} borderLeft="3px solid" borderColor="blue.300">
                                                            <HStack justify="space-between" flexWrap="wrap" spacing={2}>
                                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">{lg.acao || lg.endpoint}</Text>
                                                                <Text fontSize="10px" color="gray.400" flexShrink={0}>{lg.data ? new Date(lg.data).toLocaleString("pt-BR") : ""}</Text>
                                                            </HStack>
                                                            {lg.mensagem && <Text fontSize="xs" color="gray.600" mt={0.5}>{lg.mensagem}</Text>}
                                                            {lg.usuario && <Text fontSize="10px" color="gray.400" mt={1}>{lg.usuario}</Text>}
                                                        </Box>
                                                    ))}
                                                </VStack>
                                            )}
                                        </Secao>
                                    </VStack>
                                )}
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>
            </ModalContent>
        </Modal>
        {isML && (
            <ChatMensagensML
                isOpen={chatOpen}
                onClose={() => setChatOpen(false)}
                idOrigem={card.id_origem}
                vendaId={card.venda_id}
                packId={card.pack_id_ml ? String(card.pack_id_ml) : undefined}
                nomeCompradorInicial={det?.cliente?.nickname}
            />
        )}
        <AlertDialog isOpen={!!confirmacao} leastDestructiveRef={cancelRef} onClose={() => setConfirmacao(null)} isCentered>
            <AlertDialogOverlay>
                <AlertDialogContent mx={3}>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">{confirmacao?.titulo}</AlertDialogHeader>
                    <AlertDialogBody>{confirmacao?.mensagem}</AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={() => setConfirmacao(null)}>Cancelar</Button>
                        <Button colorScheme="orange" ml={3} onClick={() => { confirmacao?.onConfirm?.(); setConfirmacao(null); }}>Confirmar</Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialogOverlay>
        </AlertDialog>
        </>
    );
};

// ── Página ────────────────────────────────────────────────────
const PendenciasVendas = () => {
    const toast = useToast();
    const [board, setBoard] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busca, setBusca] = useState("");
    const [filtrosProblema, setFiltrosProblema] = useState([]);
    const [ordenacao, setOrdenacao] = useState("mais_atrasadas");
    const [filtroFornecedor, setFiltroFornecedor] = useState("");
    const [filtroOrigem, setFiltroOrigem] = useState("");
    const [selecionado, setSelecionado] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const carregar = useCallback(async (force = false, silencioso = false) => {
        if (!silencioso) setLoading(true);
        const res = await getPendenciasVendas(force);
        if (res?.colunas) {
            setBoard(res.colunas);
            setMeta(res.meta);
        } else {
            toast({ title: "Falha ao carregar pendências", status: "error", duration: 3000 });
        }
        setLoading(false);
    }, [toast]);

    useEffect(() => { carregar(); }, [carregar]);

    const catalogo = meta?.catalogo_problemas || {};
    const porProblema = meta?.por_problema || {};

    const toggleFiltro = (code) =>
        setFiltrosProblema((prev) => prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]);

    const temFiltro = filtrosProblema.length > 0 || busca || filtroFornecedor || filtroOrigem;
    const limparFiltros = () => { setFiltrosProblema([]); setBusca(""); setFiltroFornecedor(""); setFiltroOrigem(""); };

    // Opções de fornecedor e origem presentes no board (para os selects).
    const opcoes = useMemo(() => {
        const forn = new Set(), orig = new Set();
        if (board) {
            COLUNAS.forEach((c) => (board[c.key] || []).forEach((card) => {
                if (card.fornecedor) forn.add(card.fornecedor);
                orig.add(origemInfo(card).label);
            }));
        }
        return { fornecedores: [...forn].sort(), origens: [...orig].sort() };
    }, [board]);

    // Aplica busca + filtros (problema, fornecedor, origem)
    const boardFiltrado = useMemo(() => {
        if (!board) return null;
        const q = busca.trim().toLowerCase();
        const filtra = (card) => {
            if (filtrosProblema.length > 0) {
                const codes = (card.problemas || []).map((p) => p.code);
                if (!filtrosProblema.some((f) => codes.includes(f))) return false;
            }
            if (filtroFornecedor && card.fornecedor !== filtroFornecedor) return false;
            if (filtroOrigem && origemInfo(card).label !== filtroOrigem) return false;
            if (q) {
                const alvo = [
                    card.venda_id, card.id_origem, card.pack_id_ml, card.cliente,
                    card.produto?.nome, card.produto?.codigo_global, card.produto?.codigo_fornecedor,
                ].filter(Boolean).join(" ").toLowerCase();
                if (!alvo.includes(q)) return false;
            }
            return true;
        };
        const cmp = SORTS[ordenacao] || SORTS.mais_atrasadas;
        const out = {};
        COLUNAS.forEach((c) => { out[c.key] = (board[c.key] || []).filter(filtra).sort(cmp); });
        return out;
    }, [board, busca, filtrosProblema, ordenacao, filtroFornecedor, filtroOrigem]);

    // Move card por id (robusto a filtros)
    const aplicarMoveLocal = (vendaId, destino) => {
        setBoard((prev) => {
            if (!prev) return prev;
            const novo = {}; let alvo = null;
            COLUNAS.forEach((c) => {
                novo[c.key] = (prev[c.key] || []).filter((card) => {
                    if (card.venda_id === vendaId) { alvo = card; return false; }
                    return true;
                });
            });
            if (alvo) {
                alvo = { ...alvo, estagio: destino, resolvida: destino === "resolvido" };
                novo[destino] = [alvo, ...(novo[destino] || [])];
            }
            return novo;
        });
    };

    const moverEstagio = async (card, destino) => {
        if (card.estagio === destino) return;
        aplicarMoveLocal(card.venda_id, destino);
        setSelecionado((s) => (s && s.venda_id === card.venda_id ? { ...s, estagio: destino } : s));
        const res = await moverEstagioPendencia(card.venda_id, destino);
        if (!res) { toast({ title: "Falha ao mover", status: "error", duration: 2500 }); carregar(false, true); }
    };

    // Atualiza um campo da venda localmente (board + modal) após ação.
    const atualizarCampoLocal = (vendaId, campo, valor) => {
        setBoard((prev) => {
            if (!prev) return prev;
            const novo = {};
            COLUNAS.forEach((c) => { novo[c.key] = (prev[c.key] || []).map((cc) => cc.venda_id === vendaId ? { ...cc, [campo]: valor } : cc); });
            return novo;
        });
        setSelecionado((s) => (s && s.venda_id === vendaId ? { ...s, [campo]: valor } : s));
    };

    const alterarVenda = async (vendaId, campo, valor, apiFn, label) => {
        const ok = await apiFn(vendaId, valor);
        if (ok) {
            atualizarCampoLocal(vendaId, campo, campo === "status" ? valor : Number(valor));
            toast({ title: `${label} atualizado`, status: "success", duration: 1800, position: "top-right" });
            carregar(false, true); // re-sincroniza o quadro (observer recalcula a pendência)
        } else {
            toast({ title: `Falha ao alterar ${label.toLowerCase()}`, status: "error", duration: 2800 });
        }
    };

    const onDragEnd = (result) => {
        const { source, destination, draggableId } = result;
        if (!destination || source.droppableId === destination.droppableId) return;
        // Resolvido é terminal/automático: não é origem nem destino de movimentação manual.
        if (source.droppableId === "resolvido" || destination.droppableId === "resolvido") return;
        moverEstagio({ venda_id: Number(draggableId), estagio: source.droppableId }, destination.droppableId);
    };

    // Exporta CSV das pendências visíveis (exceto Resolvido), com "A Resolver"
    // na frente e ordenadas da mais atrasada para a menos.
    const exportarCSV = () => {
        const linhas = COLUNAS.filter((c) => c.key !== "resolvido").flatMap((c) => boardFiltrado?.[c.key] || []);
        if (linhas.length === 0) {
            toast({ title: "Nada para exportar", status: "info", duration: 2000 });
            return;
        }
        const pesoEstagio = { a_resolver: 0, em_tratativa: 1, aguardando_terceiro: 2 };
        linhas.sort((a, b) => {
            const pa = pesoEstagio[a.estagio] ?? 9, pb = pesoEstagio[b.estagio] ?? 9;
            if (pa !== pb) return pa - pb;
            return (b.dias_atraso ?? -1) - (a.dias_atraso ?? -1);
        });

        const header = ["Data", "ID Origem", "Pack ID", "ID Taga", "Origem", "Fornecedor", "Cliente", "Produto",
            "Cod Global", "Cod Fornecedor", "Valor", "Problemas", "Estagio", "Prazo Envio", "Dias Atraso"];
        const idTexto = (v) => { const s = String(v ?? ""); return s ? `="${s}"` : ""; };
        const rows = linhas.map((card) => [
            card.data_venda || "",
            idTexto(card.id_origem),
            idTexto(card.pack_id_ml),
            card.venda_id,
            origemInfo(card).label,
            card.fornecedor || "",
            card.cliente || "",
            card.produto?.nome || "",
            card.produto?.codigo_global || "",
            card.produto?.codigo_fornecedor || "",
            String(card.valor_total ?? 0).replace(".", ","),
            problemasTexto(card),
            ESTAGIO_LABEL[card.estagio] || card.estagio,
            formatPrazo(card.prazo_envio),
            card.dias_atraso ?? "",
        ]);
        const esc = (v) => {
            const s = String(v ?? "");
            return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [header, ...rows].map((r) => r.map(esc).join(";")).join("\r\n");
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pendencias-vendas-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast({ title: `${linhas.length} pendências exportadas`, status: "success", duration: 2200 });
    };

    const abrirCard = (card) => { setSelecionado(card); setModalOpen(true); };

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <Box px={{ base: 3, md: 6 }} py={4}>
                {/* Cabeçalho */}
                <Flex justify="space-between" align="center" flexWrap="wrap" gap={2} mb={3}>
                    <HStack spacing={2}>
                        <Icon as={FaExclamationTriangle} color={ACCENT} boxSize={5} />
                        <Heading size="md" color="gray.800">Pendências de Envio</Heading>
                        {meta && <Badge colorScheme="orange" fontSize="sm" px={2} borderRadius="full">{meta.total}</Badge>}
                    </HStack>
                    <HStack spacing={2}>
                        <Button size="sm" leftIcon={<FaFileCsv />} variant="outline" colorScheme="green" onClick={exportarCSV}>
                            Exportar CSV
                        </Button>
                        <Button size="sm" leftIcon={<FaSyncAlt />} variant="outline" colorScheme="orange" onClick={() => carregar(true)} isLoading={loading}>
                            Atualizar
                        </Button>
                    </HStack>
                </Flex>
                <Text fontSize="xs" color="gray.500" mb={4}>
                    Vendas abertas (confirmada / para enviar) travadas por algum problema. Arraste os cards entre as colunas conforme resolve.
                </Text>

                {/* Filtros */}
                <VStack align="stretch" spacing={3} mb={4}>
                    <HStack spacing={2} flexWrap="wrap" align="center">
                        <InputGroup size="sm" maxW={{ base: "100%", md: "320px" }}>
                            <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" boxSize="12px" /></InputLeftElement>
                            <Input
                                placeholder="Buscar por ID, cliente, produto, código…"
                                value={busca} onChange={(e) => setBusca(e.target.value)}
                                bg="white" borderRadius="lg"
                            />
                            {busca && (
                                <InputRightElement>
                                    <IconButton aria-label="Limpar busca" icon={<FaTimes />} size="xs" variant="ghost"
                                        color="gray.400" _hover={{ color: "gray.700" }} onClick={() => setBusca("")} />
                                </InputRightElement>
                            )}
                        </InputGroup>
                        <Select
                            size="sm" bg="white" borderRadius="lg" maxW={{ base: "100%", md: "210px" }}
                            value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}
                            title="Ordenar por"
                        >
                            {ORDENACOES.map((o) => (
                                <option key={o.key} value={o.key}>Ordenar: {o.label}</option>
                            ))}
                        </Select>
                        <Select
                            size="sm" bg="white" borderRadius="lg" maxW={{ base: "100%", md: "180px" }}
                            value={filtroOrigem} onChange={(e) => setFiltroOrigem(e.target.value)}
                            title="Filtrar por origem"
                        >
                            <option value="">Todas as origens</option>
                            {opcoes.origens.map((o) => (
                                <option key={o} value={o}>{o}</option>
                            ))}
                        </Select>
                        <Select
                            size="sm" bg="white" borderRadius="lg" maxW={{ base: "100%", md: "190px" }}
                            value={filtroFornecedor} onChange={(e) => setFiltroFornecedor(e.target.value)}
                            title="Filtrar por fornecedor"
                        >
                            <option value="">Todos os fornecedores</option>
                            {opcoes.fornecedores.map((f) => (
                                <option key={f} value={f}>{f}</option>
                            ))}
                        </Select>
                        {temFiltro && (
                            <Button size="sm" variant="outline" colorScheme="red" leftIcon={<FaTimes />} onClick={limparFiltros}>
                                Limpar filtros
                            </Button>
                        )}
                    </HStack>

                    {/* Chips de problema (filtro) */}
                    <Wrap spacing={1.5}>
                        {Object.entries(catalogo)
                            .filter(([code]) => (porProblema[code] || 0) > 0)
                            .map(([code, info]) => {
                                const ativo = filtrosProblema.includes(code);
                                return (
                                    <WrapItem key={code}>
                                        <Tag
                                            size="sm" borderRadius="full" cursor="pointer"
                                            variant={ativo ? "solid" : "subtle"}
                                            colorScheme={info.cor}
                                            onClick={() => toggleFiltro(code)}
                                            opacity={ativo || filtrosProblema.length === 0 ? 1 : 0.55}
                                        >
                                            <TagLabel fontSize="11px">{info.label}</TagLabel>
                                            <Box as="span" ml={1.5} fontSize="10px" fontWeight="bold"
                                                bg={ativo ? "whiteAlpha.400" : "blackAlpha.100"} px={1.5} borderRadius="full">
                                                {porProblema[code]}
                                            </Box>
                                        </Tag>
                                    </WrapItem>
                                );
                            })}
                    </Wrap>
                </VStack>

                {/* Board */}
                {loading ? (
                    <Flex justify="center" align="center" py={20}><Spinner size="xl" color={ACCENT} thickness="3px" /></Flex>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Flex gap={3} overflowX="auto" pb={3} align="stretch"
                            sx={{ "&::-webkit-scrollbar": { height: "8px" }, "&::-webkit-scrollbar-thumb": { bg: "gray.300", borderRadius: "10px" } }}>
                            {COLUNAS.map((col) => {
                                const cards = boardFiltrado?.[col.key] || [];
                                return (
                                    <Box key={col.key} minW={{ base: "82vw", sm: "300px" }} maxW={{ base: "82vw", sm: "340px" }} flex="1 0 auto">
                                        <Box bg={col.bg} borderRadius="xl" h="100%" display="flex" flexDirection="column"
                                            maxH={{ base: "calc(100dvh - 250px)", md: "calc(100vh - 240px)" }}>
                                            {/* Header coluna */}
                                            <HStack justify="space-between" px={3} py={2.5} bg={col.head} borderTopRadius="xl" position="sticky" top={0} zIndex={1}>
                                                <HStack spacing={2}>
                                                    <Box w="8px" h="8px" borderRadius="full" bg={`${col.scheme}.500`} />
                                                    <Text fontSize="sm" fontWeight="bold" color="gray.700">{col.title}</Text>
                                                </HStack>
                                                <Badge colorScheme={col.scheme} borderRadius="full" px={2}>{cards.length}</Badge>
                                            </HStack>

                                            <Droppable droppableId={col.key} isDropDisabled={col.key === "resolvido"}>
                                                {(provided, snapshot) => (
                                                    <Box
                                                        ref={provided.innerRef} {...provided.droppableProps}
                                                        flex="1" overflowY="auto" p={2} minH="120px"
                                                        bg={snapshot.isDraggingOver ? "whiteAlpha.600" : "transparent"}
                                                        transition="background 0.2s"
                                                        sx={{ "&::-webkit-scrollbar": { width: "6px" }, "&::-webkit-scrollbar-thumb": { bg: "blackAlpha.200", borderRadius: "10px" } }}
                                                    >
                                                        <VStack align="stretch" spacing={2}>
                                                            {cards.map((card, index) => (
                                                                <Draggable key={card.venda_id} draggableId={String(card.venda_id)} index={index} isDragDisabled={col.key === "resolvido"}>
                                                                    {(prov, snap) => (
                                                                        <Box ref={prov.innerRef} {...prov.draggableProps}>
                                                                            <PendenciaCard
                                                                                card={card}
                                                                                onClick={abrirCard}
                                                                                dragHandleProps={prov.dragHandleProps}
                                                                                isDragging={snap.isDragging}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {provided.placeholder}
                                                            {cards.length === 0 && (
                                                                <Flex direction="column" align="center" justify="center" py={8} color="gray.400">
                                                                    <Icon as={col.key === "resolvido" ? FaCheckCircle : FaBoxOpen} boxSize={6} mb={2} opacity={0.5} />
                                                                    <Text fontSize="xs">Vazio</Text>
                                                                </Flex>
                                                            )}
                                                        </VStack>
                                                    </Box>
                                                )}
                                            </Droppable>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Flex>
                    </DragDropContext>
                )}
            </Box>

            <DetalhesModal
                card={selecionado}
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onEstagio={moverEstagio}
                onAlterarStatus={(vid, v) => alterarVenda(vid, "status", v, setStatusVenda, "Status da venda")}
                onAlterarStatusTaga={(vid, v) => alterarVenda(vid, "status_taga", v, setStatusTaga, "Status TAGA")}
                onAlterarMarcador={(vid, v) => alterarVenda(vid, "marcador", v, setMarcadorVenda, "Marcador")}
            />
        </Box>
    );
};

export default PendenciasVendas;
